import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useTheme } from "next-themes";
import {
  LayoutDashboard,
  Package,
  Truck,
  MapPin,
  Boxes,
  FileText,
  History,
  Barcode,
  Menu,
  LogOut,
  Sun,
  Moon,
  ChevronRight,
  User,
  Users,
  ShoppingCart,
  Facebook,
  ScanBarcode,
  AlertTriangle,
  Shield,
  TrendingUp,
} from "lucide-react";
import { API } from "@/App";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/products", label: "Products", icon: Package },
  { path: "/inventory", label: "Inventory", icon: Boxes },
  { path: "/receiving", label: "Receiving", icon: FileText },
  { path: "/sales", label: "Sales/POS", icon: ShoppingCart },
  { path: "/marketplace", label: "Marketplace", icon: Facebook },
  { path: "/scanner", label: "AI Scanner", icon: ScanBarcode },
  { path: "/sellers", label: "Sellers", icon: Users },
  { path: "/suppliers", label: "Suppliers", icon: Truck },
  { path: "/locations", label: "Locations", icon: MapPin },
  { path: "/barcodes", label: "Barcodes", icon: Barcode },
  { path: "/alerts", label: "Alerts", icon: AlertTriangle },
  { path: "/forecast", label: "Forecast", icon: TrendingUp },
  { path: "/reports", label: "Reports", icon: FileText },
  { path: "/audit-logs", label: "Audit Logs", icon: History },
  { path: "/users", label: "Team", icon: Shield, adminOnly: true },
];

export default function Layout({ user, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.error(e);
    }
    navigate("/", { replace: true });
  };

  const NavContent = () => {
    const filteredItems = navItems.filter(item => {
      if (item.adminOnly && user?.role !== "admin") return false;
      return true;
    });
    return (
      <nav className="flex flex-col gap-1 p-4">
        {filteredItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`nav-item ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.path.replace("/", "")}`}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
              {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
            </Link>
          );
        })}
      </nav>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col bg-card border-r border-border">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-border">
          <img 
            src="https://customer-assets.emergentagent.com/job_ipinventory/artifacts/mxt23pqi_IMG_8675.png" 
            alt="IPVALUE Logo" 
            className="h-10 w-10 object-contain"
          />
          <div>
            <h1 className="font-heading font-bold text-lg tracking-tight">IPVALUE OS</h1>
            <p className="text-xs text-muted-foreground">Inventory System</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavContent />
        </div>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.picture} />
              <AvatarFallback>
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Header */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border bg-background/95 backdrop-blur px-4 lg:px-6">
          {/* Mobile Menu Button */}
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <div className="flex items-center gap-3 px-6 py-5 border-b border-border">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ipinventory/artifacts/mxt23pqi_IMG_8675.png" 
                  alt="IPVALUE Logo" 
                  className="h-10 w-10 object-contain"
                />
                <div>
                  <h1 className="font-heading font-bold text-lg">IPVALUE OS</h1>
                  <p className="text-xs text-muted-foreground">Inventory System</p>
                </div>
              </div>
              <NavContent />
            </SheetContent>
          </Sheet>

          <div className="flex-1" />

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle-btn"
          >
            <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem disabled>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
